<template>
  <v-dialog
    v-model="dialogProxy"
    fullscreen
    scrollable
    persistent
  >
    <v-card class="d-flex flex-column">
      <v-toolbar
        flat
        color="white"
        class="px-5 elevation-1 toolbar toolbar--compact special-rates-toolbar"
        height="48"
      >
        <div class="special-rates-toolbar__info">
          <div class="text-h5 font-weight-bold">
            Tarifa/Período
          </div>
          <div
            v-if="category && category.name"
            class="subtitle-2 grey--text"
          >
            {{ category.name }}
          </div>
        </div>
        <v-spacer></v-spacer>
        <v-btn
          icon
          class="toolbar-btn"
          @click="handleClose"
        >
          <v-icon color="error">
            {{ icons.mdiClose }}
          </v-icon>
        </v-btn>
      </v-toolbar>

      <v-divider></v-divider>

      <v-card-text class="pa-6 overflow-auto">
        <v-row>
          <v-col
            cols="12"
            md="9"
          >
            <v-alert
              type="info"
              outlined
              dense
              class="mb-6"
            >
              Defina valores diretamente no calendário. Clique em um dia para editar rapidamente ou utilize o formulário lateral para aplicar em períodos maiores.
            </v-alert>

            <v-card
              flat
              class="pa-4 calendar-wrapper"
              outlined
            >
              <div class="calendar-header d-flex justify-space-between align-center mb-4">
                <div>
                  <div class="text-h5 font-weight-bold text-capitalize">
                    {{ calendarTitle }}
                  </div>
                  <div class="text-caption grey--text">
                    Visualize e ajuste as tarifas especiais do período
                  </div>
                </div>
                <div class="d-flex align-center">
                  <v-btn
                    icon
                    class="toolbar-btn mr-1"
                    @click="calendarPrev"
                  >
                    <v-icon color="primary">
                      {{ icons.mdiChevronLeft }}
                    </v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    class="toolbar-btn mr-3"
                    @click="calendarNext"
                  >
                    <v-icon color="primary">
                      {{ icons.mdiChevronRight }}
                    </v-icon>
                  </v-btn>
                  <v-btn
                    text
                    small
                    color="primary"
                    class="mr-2"
                    @click="calendarToday"
                  >
                    Hoje
                  </v-btn>
                  <v-menu
                    v-model="monthMenu"
                    offset-y
                    :close-on-content-click="false"
                    max-width="320"
                    min-width="320"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        text
                        small
                        class="ml-2"
                        color="primary"
                        v-bind="attrs"
                        v-on="on"
                      >
                        Escolher mês
                        <v-icon
                          right
                          small
                        >
                          {{ icons.mdiCalendar }}
                        </v-icon>
                      </v-btn>
                    </template>
                    <v-date-picker
                      v-model="monthPickerValue"
                      type="month"
                      color="primary"
                      @input="selectMonth"
                    ></v-date-picker>
                  </v-menu>
                </div>
              </div>

              <v-calendar
                ref="specialCalendar"
                v-model="calendarFocus"
                type="month"
                color="primary"
                :short-weekdays="true"
                :events="calendarEvents"
                :event-color="getEventColor"
                :event-margin-bottom="8"
                :day-format="dayNumberFormat"
                :day-props="dayProps"
                @click:date="handleDateClick"
                @click:event="handleEventClick"
              >
                <template v-slot:event="{ event }">
                  <div class="tariff-event">
                    <template v-if="event.data && event.data.type === 'blocked'">
                      <div
                        class="tariff-event__pill tariff-event--blocked"
                        :class="{ 'tariff-event--selected': isDateSelected(event.start) }"
                      >
                        {{ event.name }}
                      </div>
                      <div class="tariff-event__actions">
                        <v-btn
                          icon
                          x-small
                          color="warning"
                          :loading="deletingBlockedDateId === event.data.id"
                          :disabled="blockedDatesSaving || !canEdit"
                          @click.stop="unblockDate(event.data.id)"
                        >
                          <v-icon small>
                            {{ icons.mdiLockOpenVariant }}
                          </v-icon>
                        </v-btn>
                      </div>
                    </template>
                    <template v-else>
                      <div
                        class="tariff-event__pill"
                        :class="{ 'tariff-event--selected': isDateSelected(event.start) }"
                      >
                        {{ event.name }}
                      </div>
                      <div class="tariff-event__actions">
                        <v-btn
                          icon
                          x-small
                          color="error"
                          :loading="deletingRateId === event.data.id"
                          :disabled="specialRatesSaving || !canEdit"
                          @click.stop="deleteSpecialRate(event.data.id)"
                        >
                          <v-icon small>
                            {{ icons.mdiClose }}
                          </v-icon>
                        </v-btn>
                      </div>
                    </template>
                  </div>
                </template>
              </v-calendar>
            </v-card>
          </v-col>

          <v-col
            cols="12"
            md="3"
          >
            <v-tabs
              v-model="activeTab"
              color="primary"
              class="mb-4"
              grow
            >
              <v-tab>Tarifas Especiais</v-tab>
              <v-tab>Bloqueios</v-tab>
            </v-tabs>

            <v-tabs-items v-model="activeTab">
              <!-- TAB 0: Tarifas Especiais -->
              <v-tab-item>
                <v-card
                  outlined
                  class="mb-4"
                >
                  <v-card-title class="subtitle-1 font-weight-bold">
                    Tarifa do dia selecionado
                  </v-card-title>
                  <v-card-text>
                    <div
                      v-if="selectedDate"
                      class="mb-4"
                    >
                      <div class="text-caption grey--text text--darken-1">
                        Data selecionada
                      </div>
                      <div class="text-subtitle-1 font-weight-medium text-capitalize">
                        {{ formatDateHuman(selectedDate) }}
                      </div>
                    </div>
                    <div
                      v-else
                      class="mb-4 grey--text"
                    >
                      Clique em uma data do calendário para criar ou editar rapidamente.
                    </div>
                    <v-text-field
                      v-model.number="singleRateForm.price"
                      label="Valor da diária"
                      prefix="R$"
                      type="number"
                      outlined
                      dense
                      :disabled="!selectedDate || specialRatesSaving"
                    ></v-text-field>
                    <div class="d-flex justify-space-between mt-2">
                      <v-btn
                        text
                        color="grey"
                        :disabled="!selectedDate"
                        @click="clearSelectedDate"
                      >
                        Limpar seleção
                      </v-btn>
                      <div>
                        <v-btn
                          color="primary"
                          small
                          class="mr-2"
                          :disabled="!selectedDate || specialRatesSaving || !canEdit"
                          :loading="specialRatesSaving && singleEditInProgress"
                          @click="saveSingleRate"
                        >
                          Salvar dia
                        </v-btn>
                        <v-btn
                          color="error"
                          small
                          outlined
                          :disabled="!selectedDate || !hasSpecialRate(selectedDate) || !canEdit"
                          :loading="deletingRateId && selectedDate && specialRateMap[selectedDate] && deletingRateId === specialRateMap[selectedDate].id"
                          @click="deleteSpecialRateByDate(selectedDate)"
                        >
                          Remover
                        </v-btn>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>

                <v-card outlined>
                  <v-card-title class="subtitle-1 font-weight-bold">
                    Aplicar em período/datas
                  </v-card-title>
                  <v-card-text>
                    <v-text-field
                      v-model.number="specialRateForm.price"
                      label="Valor da diária"
                      prefix="R$"
                      type="number"
                      outlined
                      dense
                      :disabled="specialRatesSaving"
                    ></v-text-field>

                    <div class="mb-2 font-weight-medium mt-4">
                      Modo de seleção
                    </div>
                    <v-btn-toggle
                      v-model="specialRateForm.mode"
                      mandatory
                      color="primary"
                      dense
                      rounded
                      class="mb-4"
                    >
                      <v-btn value="range">
                        Período contínuo
                      </v-btn>
                      <v-btn value="multiple">
                        Datas específicas
                      </v-btn>
                    </v-btn-toggle>

                    <v-slide-y-transition>
                      <div v-if="specialRateForm.mode === 'range'">
                        <v-menu
                          v-model="rangePickerMenu"
                          :close-on-content-click="false"
                          transition="scale-transition"
                          offset-y
                          max-width="290px"
                          min-width="290px"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              :value="rangeLabel"
                              label="Período"
                              readonly
                              outlined
                              dense
                              v-bind="attrs"
                              v-on="on"
                            ></v-text-field>
                          </template>
                          <v-date-picker
                            v-model="specialRateForm.range"
                            range
                            color="primary"
                          >
                            <v-spacer></v-spacer>
                            <v-btn
                              text
                              color="primary"
                              @click="rangePickerMenu = false"
                            >
                              Concluir
                            </v-btn>
                          </v-date-picker>
                        </v-menu>
                      </div>
                      <div v-else>
                        <v-menu
                          v-model="datesPickerMenu"
                          :close-on-content-click="false"
                          transition="scale-transition"
                          offset-y
                          max-width="320px"
                          min-width="320px"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              :value="datesLabel"
                              label="Datas selecionadas"
                              readonly
                              outlined
                              dense
                              v-bind="attrs"
                              v-on="on"
                            ></v-text-field>
                          </template>
                          <v-date-picker
                            v-model="specialRateForm.dates"
                            multiple
                            color="primary"
                          >
                            <v-spacer></v-spacer>
                            <v-btn
                              text
                              color="primary"
                              @click="datesPickerMenu = false"
                            >
                              Concluir
                            </v-btn>
                          </v-date-picker>
                        </v-menu>
                      </div>
                    </v-slide-y-transition>

                    <p class="text-caption grey--text mt-4">
                      Use o calendário para visualizar as tarifas já aplicadas e o seletor acima para definir novos períodos ou datas específicas.
                    </p>
                    <v-btn
                      block
                      color="primary"
                      :loading="specialRatesSaving && !singleEditInProgress"
                      :disabled="specialRatesSaving || !canEdit"
                      @click="submitSpecialRate"
                    >
                      Aplicar tarifa
                    </v-btn>
                  </v-card-text>
                </v-card>
              </v-tab-item>

              <!-- TAB 1: Bloqueios -->
              <v-tab-item>
                <v-card
                  outlined
                  class="mb-4"
                >
                  <v-card-title class="subtitle-1 font-weight-bold">
                    Dia selecionado
                  </v-card-title>
                  <v-card-text>
                    <div
                      v-if="selectedDate"
                      class="mb-4"
                    >
                      <div class="text-caption grey--text text--darken-1">
                        Data selecionada
                      </div>
                      <div class="text-subtitle-1 font-weight-medium text-capitalize">
                        {{ formatDateHuman(selectedDate) }}
                      </div>
                      <v-chip
                        v-if="isBlocked(selectedDate)"
                        color="error"
                        small
                        class="mt-2"
                      >
                        <v-icon
                          left
                          small
                        >
                          {{ icons.mdiLock }}
                        </v-icon>
                        Bloqueada
                      </v-chip>
                      <v-chip
                        v-else
                        color="success"
                        small
                        outlined
                        class="mt-2"
                      >
                        Disponível
                      </v-chip>
                    </div>
                    <div
                      v-else
                      class="mb-4 grey--text"
                    >
                      Clique em uma data do calendário para bloquear ou liberar rapidamente.
                    </div>
                    <div class="d-flex justify-space-between mt-2">
                      <v-btn
                        text
                        color="grey"
                        :disabled="!selectedDate"
                        @click="clearSelectedDate"
                      >
                        Limpar seleção
                      </v-btn>
                      <div>
                        <v-btn
                          v-if="!isBlocked(selectedDate)"
                          color="error"
                          small
                          :disabled="!selectedDate || blockedDatesSaving || !canEdit"
                          :loading="blockedDatesSaving"
                          @click="blockSelectedDate"
                        >
                          <v-icon
                            left
                            small
                          >
                            {{ icons.mdiLock }}
                          </v-icon>
                          Bloquear
                        </v-btn>
                        <v-btn
                          v-else
                          color="success"
                          small
                          outlined
                          :disabled="!selectedDate || blockedDatesSaving || !canEdit"
                          :loading="deletingBlockedDateId && selectedDate && blockedDateMap[selectedDate] && deletingBlockedDateId === blockedDateMap[selectedDate].id"
                          @click="unblockSelectedDate"
                        >
                          <v-icon
                            left
                            small
                          >
                            {{ icons.mdiLockOpenVariant }}
                          </v-icon>
                          Liberar
                        </v-btn>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>

                <v-card outlined>
                  <v-card-title class="subtitle-1 font-weight-bold">
                    Bloquear/Liberar período
                  </v-card-title>
                  <v-card-text>
                    <div class="mb-2 font-weight-medium">
                      Modo de seleção
                    </div>
                    <v-btn-toggle
                      v-model="blockForm.mode"
                      mandatory
                      color="primary"
                      dense
                      rounded
                      class="mb-4"
                    >
                      <v-btn value="range">
                        Período contínuo
                      </v-btn>
                      <v-btn value="multiple">
                        Datas específicas
                      </v-btn>
                    </v-btn-toggle>

                    <v-slide-y-transition>
                      <div v-if="blockForm.mode === 'range'">
                        <v-menu
                          v-model="blockRangePickerMenu"
                          :close-on-content-click="false"
                          transition="scale-transition"
                          offset-y
                          max-width="290px"
                          min-width="290px"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              :value="blockRangeLabel"
                              label="Período"
                              readonly
                              outlined
                              dense
                              v-bind="attrs"
                              v-on="on"
                            ></v-text-field>
                          </template>
                          <v-date-picker
                            v-model="blockForm.range"
                            range
                            color="error"
                          >
                            <v-spacer></v-spacer>
                            <v-btn
                              text
                              color="primary"
                              @click="blockRangePickerMenu = false"
                            >
                              Concluir
                            </v-btn>
                          </v-date-picker>
                        </v-menu>
                      </div>
                      <div v-else>
                        <v-menu
                          v-model="blockDatesPickerMenu"
                          :close-on-content-click="false"
                          transition="scale-transition"
                          offset-y
                          max-width="320px"
                          min-width="320px"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              :value="blockDatesLabel"
                              label="Datas selecionadas"
                              readonly
                              outlined
                              dense
                              v-bind="attrs"
                              v-on="on"
                            ></v-text-field>
                          </template>
                          <v-date-picker
                            v-model="blockForm.dates"
                            multiple
                            color="error"
                          >
                            <v-spacer></v-spacer>
                            <v-btn
                              text
                              color="primary"
                              @click="blockDatesPickerMenu = false"
                            >
                              Concluir
                            </v-btn>
                          </v-date-picker>
                        </v-menu>
                      </div>
                    </v-slide-y-transition>

                    <div class="d-flex gap-2 mt-2">
                      <v-btn
                        block
                        color="error"
                        class="mb-2"
                        :loading="blockedDatesSaving"
                        :disabled="blockedDatesSaving || !canEdit"
                        @click="submitBlockDates"
                      >
                        <v-icon left>
                          {{ icons.mdiLock }}
                        </v-icon>
                        Bloquear datas
                      </v-btn>
                    </div>
                    <div class="d-flex gap-2">
                      <v-btn
                        block
                        color="success"
                        outlined
                        :loading="blockedDatesSaving"
                        :disabled="blockedDatesSaving || !canEdit"
                        @click="submitUnblockDates"
                      >
                        <v-icon left>
                          {{ icons.mdiLockOpenVariant }}
                        </v-icon>
                        Liberar datas
                      </v-btn>
                    </div>
                  </v-card-text>
                </v-card>
              </v-tab-item>
            </v-tabs-items>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  mdiCalendar, mdiChevronLeft, mdiChevronRight, mdiClose, mdiLock, mdiLockOpenVariant,
} from '@mdi/js'

export default {
  name: 'SpecialRatesModal',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    category: {
      type: Object,
      default: () => ({ id: null, name: '' }),
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    activeTab: 0,
    specialRates: [],
    specialRatesLoading: false,
    specialRatesSaving: false,
    deletingRateId: null,
    blockedDates: [],
    blockedDatesLoading: false,
    blockedDatesSaving: false,
    deletingBlockedDateId: null,
    blockRangePickerMenu: false,
    blockDatesPickerMenu: false,
    blockForm: {
      mode: 'range',
      range: [],
      dates: [],
    },
    calendarFocus: new Date().toISOString().substr(0, 10),
    monthMenu: false,
    monthPickerValue: null,
    rangePickerMenu: false,
    datesPickerMenu: false,
    selectedDate: null,
    singleEditInProgress: false,
    singleRateForm: {
      price: null,
    },
    specialRateForm: {
      mode: 'range',
      price: null,
      range: [],
      dates: [],
    },
    icons: {
      mdiCalendar,
      mdiChevronLeft,
      mdiChevronRight,
      mdiClose,
      mdiLock,
      mdiLockOpenVariant,
    },
  }),
  computed: {
    dialogProxy: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      },
    },
    specialRateMap() {
      return this.specialRates.reduce((acc, rate) => {
        if (rate && rate.date) {
          acc[rate.date] = rate
        }

        return acc
      }, {})
    },
    blockedDateMap() {
      return this.blockedDates.reduce((acc, blocked) => {
        if (blocked && blocked.date) {
          acc[blocked.date] = blocked
        }

        return acc
      }, {})
    },
    calendarEvents() {
      const rateEvents = this.specialRates.map(rate => ({
        name: `R$ ${Number(rate.price).toFixed(2)}`,
        start: rate.date,
        end: rate.date,
        color: this.getEventColor(rate),
        data: { ...rate, type: 'rate' },
      }))
      const blockedEvents = this.blockedDates.map(blocked => ({
        name: 'Bloqueado',
        start: blocked.date,
        end: blocked.date,
        color: 'red lighten-4',
        data: { ...blocked, type: 'blocked' },
      }))

      return [...rateEvents, ...blockedEvents]
    },
    blockRangeLabel() {
      const [start, end] = Array.isArray(this.blockForm.range)
        ? this.blockForm.range
        : [this.blockForm.range?.start, this.blockForm.range?.end]
      if (!start || !end) return ''
      const startDate = new Date(`${start}T00:00:00`)
      const endDate = new Date(`${end}T00:00:00`)

      return `${startDate.toLocaleDateString('pt-BR')} - ${endDate.toLocaleDateString('pt-BR')}`
    },
    blockDatesLabel() {
      if (!this.blockForm.dates || !this.blockForm.dates.length) {
        return ''
      }

      return this.blockForm.dates
        .map(d => new Date(`${d}T00:00:00`).toLocaleDateString('pt-BR'))
        .join(', ')
    },
    calendarTitle() {
      if (!this.calendarFocus) return ''
      const date = new Date(`${this.calendarFocus}T00:00:00`)

      return date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
    },
    rangeLabel() {
      const [start, end] = Array.isArray(this.specialRateForm.range)
        ? this.specialRateForm.range
        : [this.specialRateForm.range?.start, this.specialRateForm.range?.end]
      if (!start || !end) return ''
      const startDate = new Date(`${start}T00:00:00`)
      const endDate = new Date(`${end}T00:00:00`)

      return `${startDate.toLocaleDateString('pt-BR')} - ${endDate.toLocaleDateString('pt-BR')}`
    },
    datesLabel() {
      if (!this.specialRateForm.dates || !this.specialRateForm.dates.length) {
        return ''
      }

      return this.specialRateForm.dates
        .map(d => new Date(`${d}T00:00:00`).toLocaleDateString('pt-BR'))
        .join(', ')
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.initializeModal()
      } else {
        this.resetState()
      }
    },
    'category.id': function (newId, oldId) {
      if (this.value && newId && newId !== oldId) {
        this.initializeModal()
      }
    },
    monthMenu(value) {
      if (value) {
        this.monthPickerValue = this.calendarFocus
          ? this.calendarFocus.slice(0, 7)
          : null
      }
    },
  },
  methods: {
    initializeModal() {
      if (!this.category || !this.category.id) {
        return
      }
      this.resetSpecialRateForm()
      this.loadSpecialRates(this.category.id)
      this.loadBlockedDates(this.category.id)
    },
    resetState() {
      this.activeTab = 0
      this.specialRates = []
      this.blockedDates = []
      this.selectedDate = null
      this.singleRateForm.price = null
      this.specialRateForm = {
        mode: 'range',
        price: null,
        range: [],
        dates: [],
      }
      this.blockForm = {
        mode: 'range',
        range: [],
        dates: [],
      }
      this.calendarFocus = new Date().toISOString().substr(0, 10)
      this.monthPickerValue = this.calendarFocus.slice(0, 7)
      this.deletingRateId = null
      this.deletingBlockedDateId = null
      this.specialRatesSaving = false
      this.specialRatesLoading = false
      this.blockedDatesSaving = false
      this.blockedDatesLoading = false
      this.singleEditInProgress = false
      this.monthMenu = false
      this.rangePickerMenu = false
      this.datesPickerMenu = false
      this.blockRangePickerMenu = false
      this.blockDatesPickerMenu = false
    },
    handleClose() {
      this.$emit('input', false)
      this.$emit('closed')
      this.resetState()
    },
    normalizeDateValue(value) {
      if (!value) return null
      if (typeof value === 'string') {
        if (value.length >= 10) {
          return value.slice(0, 10)
        }
        if (value.length === 7) {
          return `${value}-01`
        }

        return value
      }
      const parsed = new Date(value)
      if (Number.isNaN(parsed.getTime())) {
        return null
      }

      return parsed.toISOString().slice(0, 10)
    },
    hasSpecialRate(date) {
      const key = this.normalizeDateValue(date)

      return Boolean(key && this.specialRateMap[key])
    },
    isDateSelected(date) {
      const key = this.normalizeDateValue(date)

      return Boolean(this.selectedDate && key === this.selectedDate)
    },
    selectCalendarDate(date) {
      const normalized = this.normalizeDateValue(date)
      if (!normalized) {
        return
      }
      this.selectedDate = normalized
      const rate = this.specialRateMap[normalized]
      this.singleRateForm.price = rate ? parseFloat(rate.price) : null
    },
    clearSelectedDate() {
      this.selectedDate = null
      this.singleRateForm.price = null
    },
    deleteSpecialRateByDate(date) {
      const key = this.normalizeDateValue(date)
      const rate = key ? this.specialRateMap[key] : null
      if (!rate) {
        this.$toast.info('Nenhuma tarifa especial cadastrada para esta data')

        return
      }
      this.deleteSpecialRate(rate.id)
    },
    formatDateHuman(date) {
      const normalized = this.normalizeDateValue(date)
      if (!normalized) return ''
      const parsed = new Date(`${normalized}T00:00:00`)
      if (Number.isNaN(parsed.getTime())) {
        return normalized
      }

      return parsed.toLocaleDateString('pt-BR', {
        weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
      })
    },
    prefillSelectedDatePrice() {
      if (!this.selectedDate) {
        return
      }
      const rate = this.specialRateMap[this.selectedDate]
      this.singleRateForm.price = rate ? parseFloat(rate.price) : null
    },
    adjustCalendarMonth(offset) {
      const base = this.normalizeDateValue(this.calendarFocus) || new Date().toISOString().slice(0, 10)
      const date = new Date(`${base}T00:00:00`)
      date.setUTCDate(1)
      date.setUTCMonth(date.getUTCMonth() + offset)
      this.calendarFocus = date.toISOString().slice(0, 10)
      this.monthPickerValue = this.calendarFocus.slice(0, 7)
    },
    calendarPrev() {
      this.adjustCalendarMonth(-1)
    },
    calendarNext() {
      this.adjustCalendarMonth(1)
    },
    calendarToday() {
      this.calendarFocus = new Date().toISOString().substr(0, 10)
      this.monthPickerValue = this.calendarFocus.slice(0, 7)
    },
    selectMonth(value) {
      const normalized = this.normalizeDateValue(value)
      if (normalized) {
        this.calendarFocus = normalized
      }
      this.monthMenu = false
    },
    dayNumberFormat({ day }) {
      return day
    },
    dayProps({ date, outside }) {
      return {
        class: [
          outside ? 'outside-day' : '',
          this.isDateSelected(date) ? 'selected-day' : '',
        ].filter(Boolean).join(' '),
      }
    },
    handleDateClick(payload) {
      if (!payload) return
      const targetDate = payload.date || payload.day?.date || payload.nativeEvent?.date
      if (!targetDate) {
        return
      }
      this.selectCalendarDate(targetDate)
    },
    handleEventClick({ event }) {
      if (!event) return
      this.selectCalendarDate(event.start)
    },
    getEventColor(rateOrEvent) {
      if (rateOrEvent.data?.type === 'blocked' || rateOrEvent.type === 'blocked') {
        return 'red lighten-4'
      }
      const value = rateOrEvent.price || rateOrEvent.data?.price || rateOrEvent

      return Number(value) > 0 ? 'light-blue lighten-4' : 'grey lighten-2'
    },
    resetSpecialRateForm() {
      this.specialRateForm = {
        mode: 'range',
        price: null,
        range: [],
        dates: [],
      }
      this.clearSelectedDate()
    },
    async loadSpecialRates(categoryId) {
      if (!categoryId) {
        return
      }
      this.specialRatesLoading = true
      try {
        const response = await this.$http.get(`system/booking-engine-config/room-categories/${categoryId}/special-rates`)
        if (response.data.success) {
          this.specialRates = response.data.data.rates || []
          if (this.specialRates.length) {
            this.calendarFocus = this.specialRates[0].date
            this.monthPickerValue = this.calendarFocus.slice(0, 7)
          } else {
            this.calendarFocus = new Date().toISOString().substr(0, 10)
            this.monthPickerValue = this.calendarFocus.slice(0, 7)
          }
          this.prefillSelectedDatePrice()
        }
      } catch (error) {
        console.error('Erro ao carregar tarifas especiais:', error)
        this.$toast.error('Erro ao carregar tarifas especiais')
      } finally {
        this.specialRatesLoading = false
      }
    },
    extractSelectedDates() {
      if (this.specialRateForm.mode === 'multiple') {
        return (this.specialRateForm.dates || [])
          .map(date => this.normalizeDateValue(date))
          .filter(Boolean)
          .sort()
      }

      const range = this.specialRateForm.range || []
      const start = Array.isArray(range) ? range[0] : range?.start
      const end = Array.isArray(range) ? range[1] : range?.end
      if (!start || !end) {
        return []
      }

      let startDate = new Date(`${start}T00:00:00Z`)
      let endDate = new Date(`${end}T00:00:00Z`)

      if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
        return []
      }

      if (startDate > endDate) {
        const temp = startDate
        startDate = endDate
        endDate = temp
      }

      const dates = []
      const current = new Date(startDate)

      while (current <= endDate) {
        dates.push(current.toISOString().slice(0, 10))
        current.setUTCDate(current.getUTCDate() + 1)
      }

      return dates
    },
    async saveSingleRate() {
      if (!this.category?.id || !this.selectedDate) {
        this.$toast.error('Selecione uma data no calendário')

        return
      }

      const normalizedPrice = parseFloat(this.singleRateForm.price)
      if (!normalizedPrice || normalizedPrice <= 0) {
        this.$toast.error('Informe o valor da diária para este dia')

        return
      }

      this.singleEditInProgress = true
      this.specialRatesSaving = true
      try {
        await this.$http.post(
          `system/booking-engine-config/room-categories/${this.category.id}/special-rates`,
          {
            price: normalizedPrice,
            dates: [this.selectedDate],
          },
        )
        this.$toast.success('Tarifa diária atualizada com sucesso')
        await this.loadSpecialRates(this.category.id)
        this.$emit('updated')
      } catch (error) {
        console.error('Erro ao salvar tarifa diária:', error)
        const message = error.response?.data?.message || 'Erro ao salvar tarifa do dia'
        this.$toast.error(message)
      } finally {
        this.singleEditInProgress = false
        this.specialRatesSaving = false
      }
    },
    async submitSpecialRate() {
      if (!this.category?.id) {
        this.$toast.error('Selecione uma categoria')

        return
      }

      const normalizedPrice = parseFloat(this.specialRateForm.price)
      if (!normalizedPrice || normalizedPrice <= 0) {
        this.$toast.error('Informe o valor da diária')

        return
      }

      const dates = this.extractSelectedDates()
      if (!dates.length) {
        this.$toast.error('Selecione ao menos uma data')

        return
      }

      this.singleEditInProgress = false
      this.specialRatesSaving = true
      try {
        await this.$http.post(
          `system/booking-engine-config/room-categories/${this.category.id}/special-rates`,
          {
            price: normalizedPrice,
            dates,
          },
        )
        this.$toast.success('Tarifa especial aplicada com sucesso')
        await this.loadSpecialRates(this.category.id)
        this.$emit('updated')
        this.resetSpecialRateForm()
      } catch (error) {
        console.error('Erro ao salvar tarifa especial:', error)
        const message = error.response?.data?.message || 'Erro ao salvar tarifa especial'
        this.$toast.error(message)
      } finally {
        this.specialRatesSaving = false
      }
    },
    async deleteSpecialRate(rateId) {
      if (!this.category?.id || !rateId) {
        return
      }
      this.deletingRateId = rateId
      try {
        await this.$http.delete(
          `system/booking-engine-config/room-categories/${this.category.id}/special-rates/${rateId}`,
        )
        this.$toast.success('Tarifa especial removida')
        this.specialRates = this.specialRates.filter(rate => rate.id !== rateId)
        this.prefillSelectedDatePrice()
        this.$emit('updated')
      } catch (error) {
        console.error('Erro ao remover tarifa especial:', error)
        const message = error.response?.data?.message || 'Erro ao remover tarifa especial'
        this.$toast.error(message)
      } finally {
        this.deletingRateId = null
      }
    },
    isBlocked(date) {
      const key = this.normalizeDateValue(date)

      return Boolean(key && this.blockedDateMap[key])
    },
    async loadBlockedDates(categoryId) {
      if (!categoryId) return
      this.blockedDatesLoading = true
      try {
        const response = await this.$http.get(
          `system/booking-engine-config/room-categories/${categoryId}/blocked-dates`,
        )
        if (response.data.success) {
          this.blockedDates = response.data.data.blocked_dates || []
        }
      } catch (error) {
        console.error('Erro ao carregar datas bloqueadas:', error)
        this.$toast.error('Erro ao carregar datas bloqueadas')
      } finally {
        this.blockedDatesLoading = false
      }
    },
    async blockSelectedDate() {
      if (!this.category?.id || !this.selectedDate) {
        this.$toast.error('Selecione uma data no calendário')

        return
      }
      this.blockedDatesSaving = true
      try {
        await this.$http.post(
          `system/booking-engine-config/room-categories/${this.category.id}/blocked-dates`,
          { dates: [this.selectedDate] },
        )
        this.$toast.success('Data bloqueada com sucesso')
        await this.loadBlockedDates(this.category.id)
        this.$emit('updated')
      } catch (error) {
        console.error('Erro ao bloquear data:', error)
        const message = error.response?.data?.message || 'Erro ao bloquear data'
        this.$toast.error(message)
      } finally {
        this.blockedDatesSaving = false
      }
    },
    async unblockSelectedDate() {
      if (!this.category?.id || !this.selectedDate) return
      const blocked = this.blockedDateMap[this.selectedDate]
      if (!blocked) return
      await this.unblockDate(blocked.id)
    },
    async unblockDate(blockedDateId) {
      if (!this.category?.id || !blockedDateId) return
      this.deletingBlockedDateId = blockedDateId
      try {
        await this.$http.delete(
          `system/booking-engine-config/room-categories/${this.category.id}/blocked-dates/${blockedDateId}`,
        )
        this.$toast.success('Data liberada com sucesso')
        this.blockedDates = this.blockedDates.filter(b => b.id !== blockedDateId)
        this.$emit('updated')
      } catch (error) {
        console.error('Erro ao liberar data:', error)
        const message = error.response?.data?.message || 'Erro ao liberar data'
        this.$toast.error(message)
      } finally {
        this.deletingBlockedDateId = null
      }
    },
    extractBlockFormDates() {
      if (this.blockForm.mode === 'multiple') {
        return (this.blockForm.dates || [])
          .map(date => this.normalizeDateValue(date))
          .filter(Boolean)
          .sort()
      }

      const range = this.blockForm.range || []
      const start = Array.isArray(range) ? range[0] : range?.start
      const end = Array.isArray(range) ? range[1] : range?.end
      if (!start || !end) return []

      let startDate = new Date(`${start}T00:00:00Z`)
      let endDate = new Date(`${end}T00:00:00Z`)

      if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return []

      if (startDate > endDate) {
        const temp = startDate
        startDate = endDate
        endDate = temp
      }

      const dates = []
      const current = new Date(startDate)
      while (current <= endDate) {
        dates.push(current.toISOString().slice(0, 10))
        current.setUTCDate(current.getUTCDate() + 1)
      }

      return dates
    },
    async submitBlockDates() {
      if (!this.category?.id) return
      const dates = this.extractBlockFormDates()
      if (!dates.length) {
        this.$toast.error('Selecione ao menos uma data')

        return
      }
      this.blockedDatesSaving = true
      try {
        await this.$http.post(
          `system/booking-engine-config/room-categories/${this.category.id}/blocked-dates`,
          { dates },
        )
        this.$toast.success(`${dates.length} data(s) bloqueada(s) com sucesso`)
        await this.loadBlockedDates(this.category.id)
        this.$emit('updated')
        this.blockForm = { mode: this.blockForm.mode, range: [], dates: [] }
      } catch (error) {
        console.error('Erro ao bloquear datas:', error)
        const message = error.response?.data?.message || 'Erro ao bloquear datas'
        this.$toast.error(message)
      } finally {
        this.blockedDatesSaving = false
      }
    },
    async submitUnblockDates() {
      if (!this.category?.id) return
      const dates = this.extractBlockFormDates()
      if (!dates.length) {
        this.$toast.error('Selecione ao menos uma data para liberar')

        return
      }
      const toUnblock = this.blockedDates.filter(b => dates.includes(b.date))
      if (!toUnblock.length) {
        this.$toast.info('Nenhuma das datas selecionadas está bloqueada')

        return
      }
      this.blockedDatesSaving = true
      try {
        await Promise.all(toUnblock.map(b => this.$http.delete(
          `system/booking-engine-config/room-categories/${this.category.id}/blocked-dates/${b.id}`,
        )))
        this.$toast.success(`${toUnblock.length} data(s) liberada(s) com sucesso`)
        const unlockedIds = toUnblock.map(b => b.id)
        this.blockedDates = this.blockedDates.filter(b => !unlockedIds.includes(b.id))
        this.$emit('updated')
        this.blockForm = { mode: this.blockForm.mode, range: [], dates: [] }
      } catch (error) {
        console.error('Erro ao liberar datas:', error)
        const message = error.response?.data?.message || 'Erro ao liberar datas'
        this.$toast.error(message)
      } finally {
        this.blockedDatesSaving = false
      }
    },
  },
}
</script>

<style scoped>
.calendar-wrapper {
  background-color: #fff;
  min-height: 540px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.tariff-event {
  display: flex;
  align-items: stretch;
  width: 100%;
  box-sizing: border-box;
}

.tariff-event__pill {
  flex: 1;
  background: linear-gradient(90deg, #e3f2fd, #f1f8ff);
  border-radius: 8px;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: #0d47a1;
  border: 1px solid rgba(33, 150, 243, 0.2);
  box-shadow: 0 4px 12px rgba(13, 71, 161, 0.08);
}

.tariff-event__pill.tariff-event--selected {
  border-color: rgba(63, 81, 181, 0.7);
  box-shadow: 0 0 0 1px rgba(63, 81, 181, 0.2);
}

.tariff-event__pill.tariff-event--blocked {
  background: linear-gradient(90deg, #ffebee, #fff3f3);
  color: #b71c1c;
  border: 1px solid rgba(244, 67, 54, 0.3);
  box-shadow: 0 4px 12px rgba(183, 28, 28, 0.08);
}

.tariff-event__actions {
  display: flex;
  align-items: center;
}

.calendar-wrapper ::v-deep .v-calendar-weekly__day-label,
.calendar-wrapper ::v-deep .v-calendar-weekly__day {
  align-items: flex-start;
}

.calendar-wrapper ::v-deep .outside-day {
  opacity: 0.35;
}

.calendar-wrapper ::v-deep .selected-day .v-calendar-weekly__day-interval {
  border-radius: 12px;
  background: rgba(63, 81, 181, 0.08);
}

.calendar-wrapper ::v-deep .v-calendar-weekly {
  flex: 1;
}

.calendar-wrapper ::v-deep .v-event {
  min-height: 34px;
  width: 100%;
}

.calendar-wrapper ::v-deep .v-event > div {
  height: auto !important;
}

.tariff-event__value {
  flex: 1;
  text-align: center;
  line-height: 1.2;
}

.calendar-header .v-btn {
  text-transform: none;
}

.toolbar .toolbar-btn {
  color: inherit;
}

.toolbar .toolbar-btn:hover {
  background-color: rgba(69, 90, 100, 0.08);
}

.toolbar--compact {
  min-height: 48px;
}

.special-rates-toolbar {
  padding-top: 12px;
  padding-bottom: 12px;
}

.special-rates-toolbar .text-h5 {
  font-size: 22px;
  line-height: 1.1;
}

.special-rates-toolbar .subtitle-2 {
  font-size: 13px;
}

.special-rates-toolbar__info {
  padding: 6px 0;
}
</style>
